import styled, { css, keyframes, RuleSet } from 'styled-components';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { ToastVariant } from './types';

const slideIn = keyframes`
  from {
    transform: translateX(calc(100% + 1rem));
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(100% + 1rem));
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

export const ToastViewportStyled = styled(ToastPrimitives.Viewport)`
  position: fixed;
  top: 0;
  z-index: 100;
  display: flex;
  max-height: 100vh;
  width: 100%;
  flex-direction: column-reverse;
  padding: ${({ theme }) => theme.spacing[4]};

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    bottom: 0;
    right: 0;
    top: auto;
    flex-direction: column;
    max-width: 420px;
  }
`;

// Close button defined first to avoid circular reference
export const ToastCloseStyled = styled(ToastPrimitives.Close)`
  position: absolute;
  right: ${({ theme }) => theme.spacing[2]};
  top: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
  background: transparent;
  border: none;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }

  &:focus {
    opacity: 1;
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const variantStyles: Record<ToastVariant, RuleSet> = {
  default: css`
    background-color: ${({ theme }) => theme.colors.background};
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
  `,
  destructive: css`
    background-color: ${({ theme }) => theme.colors.danger};
    border: 1px solid ${({ theme }) => theme.colors.dangerDark};
    color: white;

    ${ToastCloseStyled} {
      color: rgba(255, 255, 255, 0.7);

      &:hover {
        color: white;
      }

      &:focus {
        box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.dangerDark};
      }
    }
  `,
  success: css`
    background-color: ${({ theme }) => theme.colors.successLight};
    border: 1px solid ${({ theme }) => theme.colors.success};
    color: ${({ theme }) => theme.colors.successDark};

    ${ToastCloseStyled} {
      color: ${({ theme }) => theme.colors.successDark};

      &:hover {
        color: ${({ theme }) => theme.colors.success};
      }

      &:focus {
        box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.success};
      }
    }
  `,
};

export const ToastRootStyled = styled(ToastPrimitives.Root)<{ $variant: ToastVariant }>`
  pointer-events: auto;
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[6]} ${theme.spacing[8]} ${theme.spacing[6]} ${theme.spacing[6]}`};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transition: all 0.2s ease-in-out;

  &[data-state='open'] {
    animation: ${slideIn} 0.2s ease-out;
  }

  &[data-state='closed'] {
    animation: ${slideOut} 0.2s ease-in, ${fadeOut} 0.2s ease-in;
  }

  &[data-swipe='move'] {
    transform: translateX(var(--radix-toast-swipe-move-x));
  }

  &[data-swipe='cancel'] {
    transform: translateX(0);
  }

  &[data-swipe='end'] {
    transform: translateX(var(--radix-toast-swipe-end-x));
    animation: ${slideOut} 0.2s ease-out;
  }

  ${({ $variant }) => variantStyles[$variant]}

  &:hover ${ToastCloseStyled} {
    opacity: 1;
  }
`;

export const ToastActionStyled = styled(ToastPrimitives.Action)`
  display: inline-flex;
  height: 32px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid transparent;
  background-color: transparent;
  padding: ${({ theme }) => `0 ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary}20;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }
`;

export const ToastTitleStyled = styled(ToastPrimitives.Title)`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

export const ToastDescriptionStyled = styled(ToastPrimitives.Description)`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  opacity: 0.9;
`;
